import { move } from '@dnd-kit/helpers'
import { StateCreator, createStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import type { UUID } from '@/types/Id'
import type { TagType } from '@/types/Tag'

import type { EditableTag } from './types'

export interface EditTagsDialogStoreState {
  editableTags: EditableTag[]
  selectedTagId: UUID | null
  deletedTagIds: UUID[]
  initialTags: EditableTag[]
  validationErrors: Record<UUID, string>
  tagType: TagType

  // Actions
  init: (tags: EditableTag[]) => void
  selectTag: (tagId: UUID) => void
  updateTagDetails: (tagId: UUID, details: Partial<EditableTag>) => void
  addNewTag: (newTag: EditableTag) => void
  markTagForDeletion: (tagId: UUID) => void
  moveTag: (
    source: Parameters<typeof move>[1],
    target: Parameters<typeof move>[2],
  ) => void
  hasAnyChanges: () => boolean
  getTagChangeStatus: (tagId: UUID) => {
    hasChanges: boolean
    isNew: boolean
  }
  validateTags: () => void
  clearDirtyState: () => void
  reset: () => void
}

export interface InitEditTagsDialogStoreArgs {
  tags: EditableTag[]
  initialSelectedTagId?: UUID
  tagType?: TagType
}

const INIT_EDIT_TAGS_DIALOG_STORE: (
  args: InitEditTagsDialogStoreArgs,
) => StateCreator<EditTagsDialogStoreState> = (args) => (set, get) => ({
  editableTags: args.tags?.map((t) => ({ ...t, isNew: false })) ?? [],
  selectedTagId:
    args.initialSelectedTagId ??
    (args.tags && args.tags.length > 0 ? args.tags[0].id : null),
  deletedTagIds: [],
  initialTags: args.tags ?? [],
  validationErrors: {},
  tagType: args.tagType ?? 'organization',

  init: (tags: EditableTag[]) => {
    const initialSelectedTagId =
      args.initialSelectedTagId ??
      (args.tags && args.tags.length > 0 ? args.tags[0].id : null)
    set({
      initialTags: tags,
      editableTags: tags.map((t) => ({ ...t, isNew: false })),
      deletedTagIds: [],
      validationErrors: {},
      selectedTagId: initialSelectedTagId,
    })
  },

  selectTag: (tagId: UUID) => set({ selectedTagId: tagId }),

  updateTagDetails: (tagId: UUID, details: Partial<EditableTag>) =>
    set((state) => {
      state.editableTags = state.editableTags.map((t) =>
        t.id === tagId ? { ...t, ...details } : t,
      )

      // Trigger validation after update
      const nameGroups: Record<string, EditableTag[]> = {}
      const errors: Record<UUID, string> = {}

      state.editableTags.forEach((tag) => {
        const normalizedName = tag.name.toLowerCase().trim()
        if (normalizedName) {
          if (!nameGroups[normalizedName]) {
            nameGroups[normalizedName] = []
          }
          nameGroups[normalizedName].push(tag)
        }
      })

      // Find duplicates
      Object.values(nameGroups).forEach((tags) => {
        if (tags.length > 1) {
          tags.forEach((tag) => {
            errors[tag.id] = `Tag name "${tag.name}" is already used`
          })
        }
      })

      state.validationErrors = errors
      return state
    }),
  addNewTag: (newTag: EditableTag) =>
    set((state) => {
      state.editableTags.push(newTag)

      // Trigger validation after adding
      const nameGroups: Record<string, EditableTag[]> = {}
      const errors: Record<UUID, string> = {}

      state.editableTags.forEach((tag) => {
        const normalizedName = tag.name.toLowerCase().trim()
        if (normalizedName) {
          if (!nameGroups[normalizedName]) {
            nameGroups[normalizedName] = []
          }
          nameGroups[normalizedName].push(tag)
        }
      })

      // Find duplicates
      Object.values(nameGroups).forEach((tags) => {
        if (tags.length > 1) {
          tags.forEach((tag) => {
            errors[tag.id] = `Tag name "${tag.name}" is already used`
          })
        }
      })

      state.validationErrors = errors
      return state
    }),

  markTagForDeletion: (tagId: UUID) =>
    set((state) => {
      const tag = state.editableTags.find((t) => t.id === tagId)
      const remainingTags = state.editableTags.filter((t) => t.id !== tagId)

      state.editableTags = remainingTags

      if (tag && !tag.isNew) {
        state.deletedTagIds.push(tagId)
      }

      if (state.selectedTagId === tagId) {
        state.selectedTagId =
          remainingTags.length > 0 ? remainingTags[0].id : null
      }

      return state
    }),

  moveTag: (source, target) =>
    set((state) => {
      // dnd-kit's move() is oscillation-safe across repeated dragOver events
      // (it reconciles via the projected sortable index); computing it here
      // against the store's current array also avoids stale render-captured
      // snapshots.
      state.editableTags = move(state.editableTags, source, target)
      return state
    }),

  clearDirtyState: () =>
    set((state) => {
      state.editableTags = state.editableTags.map((t) => ({
        ...t,
        isNew: false,
      }))
      state.deletedTagIds = []
      state.validationErrors = {}
      state.initialTags = state.editableTags.filter((t) => t.name.trim() !== '')
      return state
    }),

  hasAnyChanges: () => {
    const state = get()

    // Check for deleted tags
    if (state.deletedTagIds.length > 0) return true

    // Check for new tags
    if (state.editableTags.some((tag) => tag.isNew)) return true

    // Check for manual reordering
    const currentIdSequence = state.editableTags.map((tag) => tag.id).join('|')
    const initialIdSequence = state.initialTags.map((tag) => tag.id).join('|')
    if (currentIdSequence !== initialIdSequence) return true

    // Check for tag detail changes
    const initialTagsById: Record<UUID, EditableTag> = {}
    state.initialTags.forEach((tag) => {
      initialTagsById[tag.id] = tag
    })
    return state.editableTags.some((tag) => {
      if (tag.isNew) return false // Already checked above
      const initial = initialTagsById[tag.id]
      return (
        !initial ||
        tag.name !== initial.name ||
        tag.color !== initial.color ||
        tag.description !== initial.description
      )
    })
  },

  getTagChangeStatus: (tagId: UUID) => {
    const state = get()
    const tag = state.editableTags.find((t) => t.id === tagId)

    if (!tag) {
      return { hasChanges: false, isNew: false }
    }

    const isNew = tag.isNew

    if (isNew) {
      return { hasChanges: true, isNew: true }
    }

    // Check for tag detail changes
    const initial = state.initialTags.find((t) => t.id === tagId)
    const hasDetailChanges =
      !initial ||
      tag.name !== initial.name ||
      tag.color !== initial.color ||
      tag.description !== initial.description

    return {
      hasChanges: hasDetailChanges,
      isNew: false,
    }
  },

  validateTags: () =>
    set((state) => {
      const errors: Record<UUID, string> = {}

      // Check for duplicate names
      const nameGroups: Record<string, EditableTag[]> = {}

      state.editableTags.forEach((tag) => {
        const normalizedName = tag.name.toLowerCase().trim()
        if (normalizedName) {
          if (!nameGroups[normalizedName]) {
            nameGroups[normalizedName] = []
          }
          nameGroups[normalizedName].push(tag)
        }
      })

      // Find duplicates
      Object.values(nameGroups).forEach((tags) => {
        if (tags.length > 1) {
          tags.forEach((tag) => {
            errors[tag.id] = `Tag name "${tag.name}" is already used`
          })
        }
      })

      state.validationErrors = errors
      return state
    }),

  reset: () =>
    set((state) => {
      state.editableTags = state.initialTags.map((t) => ({
        ...t,
        isNew: false,
      }))
      state.deletedTagIds = []
      state.validationErrors = {}
      state.selectedTagId =
        state.initialTags.length > 0 ? state.initialTags[0].id : null
      return state
    }),
})

export const createEditTagsDialogStore = (
  args: InitEditTagsDialogStoreArgs,
) => {
  return createStore<EditTagsDialogStoreState, [['zustand/immer', never]]>(
    immer<EditTagsDialogStoreState>(INIT_EDIT_TAGS_DIALOG_STORE(args)),
  )
}

export type EditTagsDialogStore = ReturnType<typeof createEditTagsDialogStore>
